import { useState, useCallback } from 'react'

export const useConnectionRetry = (maxRetries = 5) => {
    const [isRetrying, setIsRetrying] = useState(false)
    const [retryCount, setRetryCount] = useState(0)

    const executeWithRetry = useCallback(
        async (asyncOperation, options = {}) => {
            const { maxRetries: customMaxRetries = maxRetries, baseDelay = 1000, onRetry, onSuccess, onFailure } = options

            setIsRetrying(true)
            setRetryCount(0)

            for (let attempt = 0; attempt < customMaxRetries; attempt++) {
                try {
                    const result = await asyncOperation()
                    setIsRetrying(false)
                    setRetryCount(0)
                    onSuccess?.(result)
                    return result
                } catch (error) {
                    setRetryCount(attempt + 1)

                    if (attempt === customMaxRetries - 1) {
                        setIsRetrying(false)
                        onFailure?.(error)
                        throw new Error(`Failed after ${customMaxRetries} attempts: ${error.message}`)
                    }

                    // Exponential backoff
                    const delay = baseDelay * Math.pow(2, attempt)
                    onRetry?.(attempt + 1, delay, error)

                    await new Promise(resolve => setTimeout(resolve, delay))
                }
            }
        },
        [maxRetries]
    )

    const reset = useCallback(() => {
        setIsRetrying(false)
        setRetryCount(0)
    }, [])

    return {
        executeWithRetry,
        isRetrying,
        retryCount,
        reset,
    }
}
